<template>
  <b-modal
    ref="programModal"
    :title="`Select ${activeTab === 0 ? 'Program' : 'Course'}`"
    centered
    lazy
    scrollable
    :ok-disabled="isLoading || areCourseLoading"
    :cancel-disabled="isLoading || areCourseLoading"
    ok-title="Update"
    cancel-title="Close"
    @ok="onModalOk"
    @hide="onModalHide"
    size="lg"
  >
    <b-tabs
      ref="programModalTabs"
      v-model="activeTab"
      nav-class="nav-tabs-card bg-white"
      content-class=" "
      class="mt-0"
      id="programModalTabs"
      style="scroll-margin-top: 66px; font-size: 16.5px"
    >
      <b-tab title="Programs" class="tab-secondary">
        <div
          v-if="get(selectedProgram, 'id') !== get(student, 'enrolled_program.id')"
          class="text-muted"
          style="margin-top: 15px"
        >
          Note: Switching programs will clear the selected course.
        </div>
        <page-separator>
          <b-link v-b-toggle="'section-1'" class="accordion__toggle p-0">
            <span class="flex">Programs</span>
            <md-icon class="accordion__toggle-icon">
              {{ stepOneVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </md-icon>
          </b-link>
        </page-separator>

        <b-collapse
          id="section-1"
          accordion="section-1"
          :visible="stepOneVisible"
          style="transition: 0.5s"
          class="accordion__menu"
          @hide="stepOneVisible = false"
          @show="stepOneVisible = true"
        >
          <div class="row px-2 ma-0" v-if="areProgramsLoading">
            <div class="col-md-4 col-sm-4 mb-2" v-for="i in [1, 2, 3, 4, 5, 6]" :key="i">
              <div class="card" style="height: 90%; border-radius: 0">
                <b-skeleton-img></b-skeleton-img>
                <b-skeleton class="ml-2 my-2" width="40%"></b-skeleton>
              </div>
            </div>
          </div>
          <div class="row px-2 ma-0">
            <div class="col-md-4 col-sm-4 mb-2" v-for="program in programs" :key="get(program, 'id')">
              <div
                class="card"
                v-if="selectedProgram"
                style="cursor: pointer; height: 90%"
                @click.prevent="changeProg(program)"
              >
                <div style="position: relative">
                  <img
                    :src="program.image_url"
                    :alt="program.title"
                    style="object-fit: cover; border-radius: 0.5rem 0.5rem 0rem 0rem"
                    width="100%"
                    height="100px"
                    class="mb-2"
                  />

                  <b-badge
                    v-if="program.id === selectedProgram.id"
                    pill
                    variant="light"
                    style="position: absolute; top: 5px; right: 5px"
                  >
                    <md-icon class="text-success">check</md-icon>
                    Selected
                  </b-badge>
                  <div class="p-2">
                    {{ program.title }}
                  </div>
                </div>
              </div>
              <div
                class="card"
                v-else-if="program.id"
                style="cursor: pointer; height: 90%"
                @click.prevent="changeProg(program)"
              >
                <div style="position: relative">
                  <img
                    :src="program.image_url"
                    :alt="program.title"
                    style="object-fit: cover; border-radius: 0.5rem 0.5rem 0rem 0rem"
                    width="100%"
                    height="100px"
                    class="mb-2"
                  />

                  <b-badge
                    v-if="get(student, 'enrolled_program.id') === program.id"
                    pill
                    variant="light"
                    style="position: absolute; top: 5px; right: 5px"
                  >
                    <md-icon class="text-success">check</md-icon>
                    Selected
                  </b-badge>
                  <div class="p-2">
                    {{ program.title }}
                  </div>
                </div>
              </div>

              <div class="card" v-else style="cursor: pointer; height: 90%">
                <div style="position: relative">
                  <div
                    style="width: 100%; height: 100px; border-radius: 0.5rem 0.5rem 0rem 0rem"
                    class="bg-primary mb-2"
                  ></div>
                  <b-badge pill variant="light" style="position: absolute; top: 5px; right: 5px">
                    <md-icon class="text-success">check</md-icon>
                    Selected
                  </b-badge>
                  <div class="p-2">
                    {{ program.title }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>

        <page-separator>
          <b-link v-b-toggle="'section-2'" class="accordion__toggle p-0">
            <span class="flex">Program Tags</span>
            <md-icon class="accordion__toggle-icon">
              {{ stepTwoVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </md-icon>
          </b-link>
        </page-separator>

        <b-collapse
          id="section-2"
          accordion="section-2"
          :visible="stepTwoVisible"
          style="transition: 0.5s"
          class="accordion__menu"
          @hide="stepTwoVisible = false"
          @show="stepTwoVisible = true"
        >
          <div class="row px-3 ma-0">
            <v-select
              id="tag"
              class="form-control v-select-custom"
              label="title"
              v-model="selectedTag"
              :value="selectedTag"
              :reduce="sc => sc.id"
              placeholder="Select Program Tag"
              :options="allTags"
              :loading="areTagsLoading && !allTags.length"
              @search="fetchOptions"
              @input="changeTag"
              :disabled="$route.name === 'edit-attendance' || (areTagsLoading && !allTags.length) || !selectedProgram"
            >
              <template #list-footer>
                <li
                  v-observe-visibility="visibilityChanged"
                  v-show="allTags.length && allTags.length < totalTags"
                  class="loader"
                ></li>
              </template>
              <template slot="option" slot-scope="option">
                <span :id="`tag-${option.id}`">
                  {{ option.title }}
                </span>
              </template>
              <template slot="selected-option" slot-scope="option">
                {{ option.title }}
              </template>
            </v-select>
          </div>
        </b-collapse>
      </b-tab>
      <b-tab title="Courses" class="tab-secondary">
        <div class="row px-2 ma-0" v-if="areCourseLoading" style="margin-top: 15px">
          <div class="col-md-4 col-sm-4 mb-2" v-for="i in [1, 2, 3, 4, 5, 6]" :key="i">
            <div class="card" style="height: 90%; border-radius: 0">
              <b-skeleton-img></b-skeleton-img>
              <b-skeleton class="ml-2 my-2" width="40%"></b-skeleton>
            </div>
          </div>
        </div>
        <div v-if="courses.length === 0 && !areCourseLoading && !isLoadingMoreCourses" style="margin-top: 15px">
          <i class="text-muted">No courses available for this program.</i>
        </div>
        <div class="row px-2 ma-0" style="margin-top: 15px">
          <div class="col-md-4 col-sm-4 mb-2" v-for="course in courses" :key="get(course, 'id')">
            <div
              class="card"
              v-if="selectedCourse"
              style="cursor: pointer; height: 90%"
              @click.prevent="changeCourse(course)"
            >
              <div style="position: relative">
                <img
                  :src="course.image_url"
                  :alt="course.title"
                  style="object-fit: cover; border-radius: 0.5rem 0.5rem 0rem 0rem"
                  width="100%"
                  height="100px"
                  class="mb-2"
                />

                <b-badge
                  v-if="course.id === selectedCourse"
                  pill
                  variant="light"
                  style="position: absolute; top: 5px; right: 5px"
                >
                  <md-icon class="text-success">check</md-icon>
                  Selected
                </b-badge>
                <div class="p-2">
                  {{ course.title }}
                </div>
              </div>
            </div>
            <div
              class="card"
              v-else-if="course.id"
              style="cursor: pointer; height: 90%"
              @click.prevent="changeCourse(course)"
            >
              <div style="position: relative">
                <img
                  :src="course.image_url"
                  :alt="course.title"
                  style="object-fit: cover; border-radius: 0.5rem 0.5rem 0rem 0rem"
                  width="100%"
                  height="100px"
                  class="mb-2"
                />

                <b-badge
                  v-if="get(student, 'assigned_course.id') === course.id"
                  pill
                  variant="light"
                  style="position: absolute; top: 5px; right: 5px"
                >
                  <md-icon class="text-success">check</md-icon>
                  Selected
                </b-badge>
                <div class="p-2">
                  {{ course.title }}
                </div>
              </div>
            </div>

            <div class="card" v-else style="cursor: pointer; height: 90%">
              <div style="position: relative">
                <div
                  style="width: 100%; height: 100px; border-radius: 0.5rem 0.5rem 0rem 0rem"
                  class="bg-primary mb-2"
                ></div>
                <b-badge pill variant="light" style="position: absolute; top: 5px; right: 5px">
                  <md-icon class="text-success">check</md-icon>
                  Selected
                </b-badge>
                <div class="p-2">
                  {{ course.title }}
                </div>
              </div>
            </div>
          </div>

          <!-- Loading indicator -->
          <div v-if="isLoadingMoreCourses" class="col-12 text-center my-3">
            <b-spinner small type="grow" /> Loading more courses...
          </div>

          <!-- End of list message -->
          <!-- <div v-if="!hasMoreCourses && courses.length > 0" class="col-12 text-center my-3 text-muted">
            No more courses to load
          </div> -->

          <!-- Observer element for infinite scroll -->
          <div
            v-observe-visibility="{
              callback: visibilityChangedCourse,
              throttle: 300,
              intersection: {
                rootMargin: '0px 0px 100px 0px',
                threshold: 0.1,
              },
            }"
            class="col-12"
            style="height: 1px"
          ></div>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script>
import { get, debounce } from 'lodash';
import { mapActions } from 'vuex';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Vue from 'vue';
import { ObserveVisibility } from 'vue-observe-visibility';

Vue.directive('observe-visibility', ObserveVisibility);

export default {
  components: { PageSeparator },
  name: 'ProgramModal',

  props: {
    showModal: { type: Boolean, default: false },
    student: { type: Object, default: () => {} },
  },

  data() {
    return {
      activeTab: 0,
      programs: [],
      isProgramChanged: false,
      areProgramsLoading: false,
      selectedProgram: null,
      selectedTag: null,
      stepOneVisible: false,
      stepTwoVisible: false,
      allTags: [],
      areTagsLoading: false,
      isLoading: false,
      limit: 15,
      offset: 0,
      totalTags: 0,
      search: '',

      courses: [],
      isCourseChanged: false,
      areCourseLoading: false,
      selectedCourse: null,
      isLoadingCourse: false,
      limitCourse: 15,
      offsetCourse: 0,
      searchCourse: '',
      hasMoreCourses: true,
      isLoadingMoreCourses: false,
    };
  },

  computed: {},

  watch: {
    showModal(value) {
      if (value) {
        if (this.student.enrolled_program) {
          this.selectedProgram = this.student.enrolled_program;
          this.fetchTags();
        }

        this.selectedTag = this.student.selected_tag;
        this.fetchPrograms();

        if (this.student.assigned_course) {
          this.selectedCourse = this.student.assigned_course.id;
          this.fetchAllCourses();
        }

        this.$refs.programModal.show();
      }
    },

    selectedProgram: {
      handler() {
        this.allTags = [];
        this.offset = 0;
        this.tag_id = null;
        this.search = '';
      },
    },
    // showCourseModal(value) {
    //   if (value) {
    //     if (this.student.enrolled_program) {
    //       this.selectedProgram = this.student.enrolled_program;
    //     }
    //     if (this.student.assigned_course) {
    //       this.selectedCourse = this.student.assigned_course.id;
    //       this.fetchAllCourses();
    //     }

    //     this.$refs.courseModal.show();
    //   }
    // },

    selectedCourse: {
      handler() {
        this.offsetCourse = 0;
        this.searchCourse = '';
      },
    },
  },

  methods: {
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('student', ['updateStudentProgram', 'updateStudentTag']),
    ...mapActions('attendance', ['getTags']),
    ...mapActions('courses', ['getProgramAssignedCourses']),
    ...mapActions('student', ['updateStudentCourse']),
    get,
    changeCourse(course) {
      this.selectedCourse = course.id;
    },
    async updateCourse() {
      //const h = this.$createElement;

      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to change the course?', {
          title: 'Are you sure?',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          this.isLoadingCourse = true;
          const courseData = {
            assigned_course: this.selectedCourse,
          };
          if (this.selectedCourse !== get(this.student, 'assigned_course.id')) {
            await this.updateStudentCourse({ id: this.student.id, data: courseData });
          }
          this.isCourseChanged = true;
          this.isLoadingCourse = false;
          this.hideModalManual();
          this.makeToast({
            variant: 'success',
            msg: 'Updated Successfully!',
          });
        } else {
          this.isCourseChanged = false;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Cannot change course. Verify student registration.' });
      }
      this.isLoadingCourse = false;
    },
    visibilityChangedCourse(isVisible) {
      if (isVisible && !this.areCourseLoading && !this.isLoadingMoreCourses && this.hasMoreCourses) {
        this.fetchAllCourses(true);
      }
    },

    async fetchAllCourses(loadMore = false) {
      if (this.areCourseLoading || (loadMore && !this.hasMoreCourses)) return;

      if (!this.selectedProgram) return;

      if (!loadMore) {
        this.areCourseLoading = true;
        this.offsetCourse = 0;
        this.hasMoreCourses = true;
      } else {
        this.isLoadingMoreCourses = true;
      }

      try {
        const res = await this.getProgramAssignedCourses({
          program: this.selectedProgram.id,
          limit: this.limitCourse,
          offset: this.offsetCourse,
        });

        if (res.data) {
          if (loadMore) {
            this.courses = [...this.courses, ...res.data.results];
          } else {
            this.courses = res.data.results;
          }

          // Check if we've reached the end of the list
          if (res.data.count <= this.courses.length) {
            this.hasMoreCourses = false;
          } else {
            this.offsetCourse += this.limitCourse;
          }
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load courses. Please try again.' });
      }

      this.areCourseLoading = false;
      this.isLoadingMoreCourses = false;
    },
    changeProg(program) {
      this.selectedProgram = program;
      this.selectedTag = null;
      this.fetchTags();
      if (this.allTags.length) {
        this.stepOneVisible = false;
      }
      this.stepTwoVisible = true;
    },
    changeTag(tag) {
      this.selectedTag = tag;
    },
    async updateProgram() {
      //const h = this.$createElement;

      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          'This action will result in generation of a new transcript for the student. Are you sure you want to continue?',
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          this.isLoading = true;
          const prgData = {
            enrolled_program: this.selectedProgram.id,
          };
          const tagData = {
            selected_tag: this.selectedTag,
          };

          if (this.selectedProgram.id !== get(this.student, 'enrolled_program.id')) {
            await this.updateStudentProgram({ id: this.student.id, data: prgData });
          }
          if (this.selectedTag !== get(this.student, 'selected_tag')) {
            await this.updateStudentTag({ id: this.student.id, data: tagData });
          }

          this.isProgramChanged = true;
          this.isLoading = false;
          this.hideModalManual();
          this.makeToast({
            variant: 'success',
            msg: 'Updated Successfully!',
          });
        } else {
          this.isProgramChanged = false;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Cannot change program. Verify student registration.' });
      }
      this.isLoading = false;
    },
    visibilityChanged(reached) {
      if (reached) {
        this.offset += 15;
        this.fetchTags();
      }
    },
    async fetchTags() {
      this.tag_id = null;
      if (!this.search) this.areTagsLoading = true;
      const response = await this.getTags({
        ...(this.search && { search: this.search }),
        program__id: this.selectedProgram.id,
        limit: this.limit,
        offset: this.offset,
      });

      if (!this.allTags.length) {
        this.stepOneVisible = true;
      }

      this.allTags = this.allTags.concat(response.data.data.results);
      this.totalTags = response.data.data.count;
      this.areTagsLoading = false;

      if (this.allTags.length > 15) {
        setTimeout(() => {
          const el = document.getElementById(`tag-${this.allTags.slice(-15)[0].id}`);

          if (el) {
            el.scrollIntoView({ behavior: 'instant', block: 'nearest' });
          }
        }, 100);
      }
    },

    debouncedSearchTag: debounce(async (loading, search, vm) => {
      vm.search = search;
      const response = await vm.getTags({
        search,
        program__id: vm.selectedProgram.id,
        limit: vm.limit,
        offset: vm.offset,
      });
      loading(false);

      vm.allTags = [];
      vm.totalTags = 0;
      vm.allTags = vm.allTags.concat(response.data.data.results);
      vm.totalTags = response.data.data.count;
    }, 500),

    async fetchOptions(search, loading) {
      if (!this.tag_id) {
        this.offset = 0;
        this.allTags = [];
        this.search = search;
        loading(true);
        this.debouncedSearchTag(loading, search, this);
      }
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms({ ordering: 'id' });
        if (res.data) {
          const progs = res.data.map(program => program.id);
          if (this.student.enrolled_program) {
            if (!progs.includes(this.student.enrolled_program.id)) {
              this.programs = res.data;
              this.programs.push(this.student.enrolled_program);
            } else {
              this.programs = res.data;
            }
          } else {
            this.programs = res.data;
            this.programs.push({ id: null, title: 'Other Program' });
          }
        }
      } catch (err) {
        // Handle error if needed
      }
      this.areProgramsLoading = false;
    },
    onModalOk() {
      if (this.activeTab === 0) {
        this.updateProgram();
      } else {
        this.updateCourse();
      }
    },
    onModalHide() {
      // Only reset if not coming from a successful update
      if (!this.isProgramChanged) {
        this.hideModal();
      }
    },
    hideModal() {
      this.selectedProgram = null;
      this.selectedTag = null;
      this.stepOneVisible = false;
      this.stepTwoVisible = false;
      this.$emit('close', this.isProgramChanged);
    },

    hideModalManual() {
      this.$refs.programModal.hide();
      this.hideModal();
    },
  },
};
</script>
<style>
#programModalTabs ::v-deep .nav {
  display: flex !important;
  flex-wrap: nowrap !important;
  overflow-x: auto;
  overflow-y: hidden;
  list-style: none;
  white-space: nowrap;
  padding-left: 18px;
  padding-right: 18px;
}
</style>
